import { translate } from '@shakkerai/util-i18n';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { getPromptGuideList } from '@shakkerai/services';
import { isOverseas } from '@shakkerai/pro-utils';
import { Icon } from '@shakkerai/components';
import { useEventListener } from 'ahooks';
import { GuidePopover } from '@shakkerai/pro-components';
import { updateUnitData } from '@starflow/app/helpers/flow';

export const RecommendThemeList = React.memo(({ generator, onClose }) => {
  const itemLen = isOverseas ? 4 : 5;
  const [guideThemeList, setGuideThemelist] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const prompt = generator?.data?.prompt ?? '';

  const handleSelectTheme = (item) => {
    if (item?.prompt && prompt.indexOf(item.topic) === -1) {
      updateUnitData(generator?.id, { prompt: `${item.topic}, ${item.prompt}` });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await getPromptGuideList({
        lang: isOverseas ? 'en' : 'zh',
      });
      if (res?.data?.length > 0) {
        setGuideThemelist(res.data);
      }
    };
    fetchData();
  }, []);

  const currentDataList = useMemo(() => {
    const start = currentIdx * itemLen;
    const end = start + itemLen;
    return guideThemeList?.slice(start, end);
  }, [guideThemeList, currentIdx, itemLen]);

  const handleRefresh = () => {
    const isEnd = currentIdx * itemLen + itemLen >= guideThemeList.length;
    if (isEnd) {
      setCurrentIdx(0);
    } else {
      setCurrentIdx(currentIdx + 1);
    }
  };

  const handleClose = () => {
    setVisible(true);
  };

  const handleCloseOne = () => {
    setVisible(false);
    onClose?.();
  };

  const handleGlobalClick = useCallback((event) => {
    if (event.target?.closest('[data-toggle="close-btn-container"]')) {
      return;
    }

    if (event.target?.closest('[id="newPromptTheme"]')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  const handleCloseOver = () => {
    localStorage.setItem('__HIDDEN_PROMPT_THEME_RECOMMENDATION_FLAG___', '1');
    setVisible(false);
    onClose?.();
  };

  return (
    <div className="w-full h-[42px] mb-2 justify-between flex relative rounded-[4px] bg-white overflow-hidden border">
      <div className="flex w-full items-center px-3">
        <h3 className="leading-7 text-xs text-[#8C8C8C] flex-shrink-0">{translate('prompt_guide_topic')}</h3>
        <div className="flex flex-1 space-x-4 ml-3">
          {currentDataList.map((item, idx) => {
            return (
              <button
                key={idx}
                className={`flex items-center h-[26px] py-1 px-4 rounded-full overflow-hidden whitespace-nowrap text-ellipsis text-xs leading-[14px] bg-[#F4F6F8] text-[#2d2d2d] hover:bg-[#DBEEFF] hover:text-[#2D2D2D]`}
                onClick={() => handleSelectTheme(item)}
              >
                <span>{item.topic}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center">
          <button className="group flex items-center text-[#7F7F7F] hover:text-[#2D2D2D]" onClick={handleRefresh}>
            <Icon fontSize={18} className="icon-proshauxin group-hover:text-[#2d2d2d]" />
            <span className="text-xs translate-y-px flex-shrink-0">{translate('prompt_guide_switch')}</span>
          </button>
          <i className="inline-block w-[1px] h-[12px] mx-3 bg-[#EDEDED]"></i>
          <button
            className="flex items-center text-[#7F7F7F] hover:text-[#2D2D2D]"
            onClick={handleClose}
            data-toggle="close-btn-container"
          >
            <Icon fontSize={16} className="icon-border_close-1 hover:text-[#2D2D2D]" />
          </button>
          <GuidePopover
            id="newPromptTheme"
            visible={visible}
            title={translate('prompt_guide_close_title')}
            position="top"
            width={280}
            withArrow={false}
            showFooter={false}
            offset={{ mainAxis: 40, crossAxis: -80 }}
            content={null}
            customFooter={
              <div className="mt-[16px] flex justify-end items-center">
                <span className="text-[13px] cursor-pointer text-black underline" onClick={handleCloseOne}>
                  {translate('prompt_guide_close_once')}
                </span>
                <span
                  className="h-[26px] cursor-pointer text-center leading-[28px] px-4 ml-4 bg-[#fff] rounded-sm text-[13px] text-[#1F2329]"
                  onClick={handleCloseOver}
                >
                  {translate('prompt_guide_stop')}
                </span>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
});
